/**
 * Zentraler Frontend-Logger für das Admin-UI.
 * Sendet Errors + interessante Events gebatcht an POST /api/logs/ingest.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface QueuedEntry {
  level: LogLevel;
  message: string;
  exception?: string;
  url?: string;
  userAgent?: string;
  loggerName?: string;
  properties?: Record<string, unknown>;
}

const SOURCE = 'admin-ui';
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH = 20;
const MAX_QUEUE = 200;
const ENDPOINT = '/api/logs/ingest';

const queue: QueuedEntry[] = [];
let started = false;

function enqueue(entry: QueuedEntry): void {
  queue.push(entry);
  if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
}

function makeBatch(): QueuedEntry[] {
  return queue.splice(0, MAX_BATCH);
}

async function sendBatch(batch: QueuedEntry[]): Promise<void> {
  if (batch.length === 0) return;
  const body = JSON.stringify({
    source: SOURCE,
    entries: batch.map((e) => ({
      ...e,
      url: e.url ?? (typeof window !== 'undefined' ? window.location.href : undefined),
      userAgent: e.userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : undefined)
    }))
  });
  try {
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true
    });
  } catch {
    /* swallow */
  }
}

function flushAsBeacon(): void {
  if (queue.length === 0 || typeof navigator === 'undefined' || !navigator.sendBeacon) return;
  const batch = queue.splice(0, queue.length);
  const body = JSON.stringify({
    source: SOURCE,
    entries: batch.map((e) => ({
      ...e,
      url: e.url ?? window.location.href,
      userAgent: e.userAgent ?? navigator.userAgent
    }))
  });
  try {
    navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
  } catch {
    /* ignore */
  }
}

function start(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  setInterval(() => {
    if (queue.length > 0) sendBatch(makeBatch());
  }, FLUSH_INTERVAL_MS);

  window.addEventListener('error', (e) => {
    enqueue({
      level: 'error',
      message: e.message || 'Uncaught error',
      exception: e.error instanceof Error ? e.error.stack : undefined,
      properties: { filename: e.filename, lineno: e.lineno, colno: e.colno }
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    enqueue({
      level: 'error',
      message: 'Unhandled promise rejection',
      exception: reason instanceof Error ? reason.stack : String(reason)
    });
  });

  window.addEventListener('beforeunload', flushAsBeacon);
  window.addEventListener('pagehide', flushAsBeacon);
}

function exceptionToString(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof Error) return err.stack || err.message;
  if (typeof err === 'string') return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

export const logger = {
  start,
  debug(message: string, properties?: Record<string, unknown>): void {
    enqueue({ level: 'debug', message, properties });
  },
  info(message: string, properties?: Record<string, unknown>): void {
    enqueue({ level: 'info', message, properties });
  },
  warn(message: string, properties?: Record<string, unknown>): void {
    enqueue({ level: 'warn', message, properties });
  },
  error(message: string, error?: unknown, properties?: Record<string, unknown>): void {
    enqueue({
      level: 'error',
      message,
      exception: exceptionToString(error),
      properties
    });
  }
};
